import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { http } from '../services/http';

/**
 * 로그인 — admin 직원 계정(이메일 · 비밀번호). DEVELOPER · ADMIN 만 들어온다.
 * 모양은 GRK_project 로그인과 같다(로고 · 카드 · 문구) — 「PROJECT」 자리만 「INVESTMENT」.
 */
export const LoginPage: React.FC = () => {
  const { user, login } = useAuth();
  const state = useLocation().state as { from?: string; message?: string } | null;
  const from = state?.from ?? '/dashboard';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // 바로 이동(/sso)이 실패하면 이유를 들고 온다
  const [error, setError] = useState(state?.message ?? '');
  // 누른 뒤 응답 전까지 막는다 — project 에서 여러 번 눌렸다(#113)
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={from} replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(''); setBusy(true);
    try {
      const r = await http.post<{ accessToken: string }>('/auth/login', { email: email.trim(), password });
      await login(r.accessToken);
    } catch (x) {
      setError((x as Error).message || '로그인에 실패했습니다.');
    } finally { setBusy(false); }
  };

  const input = 'w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500';
  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-100 px-4">
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8 md:p-10">
          <div className="text-center mb-8">
            <img src="/GRK_partners_가로형.jpg" alt="GRK Partners" className="h-14 object-contain mx-auto mb-4" />
            <div className="text-sm font-medium tracking-[0.2em] text-slate-600 uppercase mb-2">INVESTMENT</div>
            <p className="text-sm text-slate-500">등록된 이메일과 비밀번호로 로그인하세요</p>
          </div>

          {error && <div role="alert" className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">{error}</div>}

          <form onSubmit={submit}>
            <div className="mb-4">
              <label htmlFor="login-email" className="block text-sm font-medium text-slate-700 mb-2">이메일</label>
              <input id="login-email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)}
                className={input} placeholder="example@grkcon.com" required />
            </div>
            <div className="mb-6">
              <label htmlFor="login-password" className="block text-sm font-medium text-slate-700 mb-2">비밀번호</label>
              <input id="login-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
                className={input} placeholder="비밀번호를 입력하세요" required />
            </div>
            <button type="submit" disabled={busy}
              className="w-full bg-indigo-600 text-white py-3 px-5 rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-60">
              {busy ? '로그인 중…' : '로그인'}
            </button>
          </form>
        </div>
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>&copy; {new Date().getFullYear()} GRK Investment. All Rights Reserved.</p>
        </div>
      </div>
    </div>
  );
};
